import { computed, observable } from 'mobx';
import { ITranslationsCollection } from '@estee/elc-service';
import {
    IBreadCrumbsDelimiter,
    IOrderStatusCode,
    IOrderStatusLabelMap,
    OrderStatusType
} from './Config';
import { PROGRESS_MILESTONES_BY_STATUS } from '../../utils/Constants';

interface IGetOrderStatusInfoMap {
    deliveryMethodName?: string;
    trackingUrl?: string;
    shippedDateFormattedString?: string;
}

// Handle order status related mapping & calculation basing on order status code from api
export class OrderStatus {
    @observable public orderStatusType: OrderStatusType;
    @observable public status?: string;
    @observable public explanation?: string;
    @observable public description?: string;
    @observable public subDescription?: string;
    @observable public progressLabels: string[] = [];
    @observable public isVerticalTMOProgressBarDisplay: boolean;
    @observable public breadCrumbDelimiter?: IBreadCrumbsDelimiter;
    @observable public statusHeader?: string;

    // Setting current step to -1 means no step is active/completed
    @observable public progressStep: number = -1;
    @observable public progressItems: { completed: boolean }[] = [];
    @observable public isShipped: boolean = false;
    @observable public formattedDeliveredDate: string;
    @observable public formatStatusTimestamp: string;
    @observable public isPartialShipment: boolean;

    @observable private orderStatusCode: string;
    @observable private translations: ITranslationsCollection;
    // Order status codes from configuration
    @observable private orderStatusCodeMap: IOrderStatusCode[];
    @observable private orderStatusLabelMap: IOrderStatusLabelMap[];
    @observable private isReturn: boolean;

    constructor(
        orderStatusCode: string,
        orderStatuses: IOrderStatusCode[] = [],
        translations: ITranslationsCollection,
        isVerticalTMOProgressBarDisplay: boolean,
        formattedDeliveredDate: string,
        orderStatusLabelMap: IOrderStatusLabelMap[],
        breadCrumbDelimiter: IBreadCrumbsDelimiter,
        isReturn: boolean,
        formatStatusTimestamp: string,
        isPartialShipment: boolean
    ) {
        this.translations = translations;
        this.orderStatusCodeMap = orderStatuses;
        this.orderStatusCode = orderStatusCode;
        this.isVerticalTMOProgressBarDisplay = isVerticalTMOProgressBarDisplay;
        this.orderStatusLabelMap = orderStatusLabelMap;
        this.formattedDeliveredDate = formattedDeliveredDate;
        this.breadCrumbDelimiter = breadCrumbDelimiter;
        this.isReturn = isReturn;
        this.isPartialShipment = isPartialShipment;
        const orderStatus =
            this.orderStatusCodeMap.find((item) => item.orderStatusCodes.includes(orderStatusCode))
                ?.orderStatusMappedType || OrderStatusType.ORDER_PROCESSING;
        const {
            status,
            explanation,
            description,
            subDescription,
            statusHeader
        } = this.getOrderStatusInfoMap()[orderStatus];

        this.orderStatusType = orderStatus;
        this.status = status;
        this.description = description;
        this.explanation = explanation;
        this.subDescription = subDescription;
        this.statusHeader = statusHeader;
        this.formatStatusTimestamp = formatStatusTimestamp;

        // The following scenarios are considered as order shipped according to:
        // https://confluence.esteeonline.com/display/AM/Build+Stardust+React+service+bundle
        this.isShipped =
            [
                OrderStatusType.SHIPPED,
                OrderStatusType.DELIVERED,
                OrderStatusType.DELAYED,
                OrderStatusType.UNDELIVERABLE,
                OrderStatusType.RETURN_IN_PROGRESS
            ].indexOf(orderStatus) >= 0;

        // Steps to be displayed
        const stepCount = this.displayStatuses.length;
        if (stepCount) {
            this.progressLabels = this.displayStatuses.map((item) => {
                return this.getOrderStatusInfoMap()[item.orderStatusMappedType]?.status || '';
            });

            // completed || currentIndex > index -> show check mark
            this.progressItems = Array(stepCount).fill({
                completed: false
            });

            let progressStep =
                this.orderStatusCodeMap.find((i) => i.orderStatusMappedType === orderStatus)
                    ?.indexOfProgress ?? -1;
            if (progressStep >= stepCount - 1) {
                // If current step is after last progress step displayed, then show at the last progress step
                progressStep = stepCount - 1;
                this.progressItems[stepCount - 1].completed = true;
            }
            this.progressStep = progressStep;
        }
    }

    private getOrderStatusInfoMap({
        deliveryMethodName,
        trackingUrl,
        shippedDateFormattedString
    }: IGetOrderStatusInfoMap = {}) {
        const {
            orderProcessing,
            orderStatusProcessingMessage,
            yourOrderIsProcessing,
            orderStatusProcessingInstruction,
            atWarehouse,
            orderStatusAtWarehouseMessage,
            yourOrderIsAtWarehouse,
            orderStatusAtWarehouseInstruction,
            shipped,
            orderStatusShippedMessage,
            yourOrderIsShipped,
            orderStatusShippedInstruction,
            delivered,
            orderStatusDeliveredMessage,
            yourOrderIsDelivered,
            orderStatusDeliveredInstruction,
            returnInProgress,
            orderStatusReturnInProgressMessage,
            yourOrderIsReturnInProgress,
            orderStatusReturnInProgressInstruction,
            returnInitiated,
            yourOrderIsReturnInitiated,
            orderHasBeenReturnInitiated,
            returnShipped,
            yourOrderIsReturnToShipped,
            orderHasBeenReturnToShipped,
            returnReceived,
            yourOrderIsReturnReceived,
            orderHasBeenReturnReceived,
            cancelled,
            orderStatusCancelledMessage,
            yourOrderIsCancelled,
            orderStatusCancelledInstruction,
            delayed,
            yourOrderIsDelayed,
            orderStatusDelayedInstruction,
            orderStatusDelayedMessage,
            orderStatus,
            estimatedDelivery,
            deliveredOn,
            unableToDeliver,
            yourOrderIsUnableToDeliver,
            orderStatusUnableToDeliverInstruction,
            orderHasBeenUnableToDeliver
        } = this.translations;

        const hasReturnOrNotReturnText = (textForNotReturn: string, textForReturn: string) => {
            return this.isReturn ? textForReturn : textForNotReturn;
        };

        const getDescriptionMessage = (textForNotReturn: string, textForReturn?: string) => {
            if (this.isVerticalTMOProgressBarDisplay && this.isReturn && textForReturn) {
                return textForReturn;
            }

            return textForNotReturn;
        };

        const getExplanationMessage = (textForNotReturn: string, textForReturn?: string) => {
            if (this.isReturn && textForReturn) {
                return textForReturn;
            }
            const orderStatusText = this.orderStatusLabelMap
                ? this.orderStatusLabelMap.find((item) =>
                      item.orderStatusCode.includes(this.orderStatusCode)
                  )
                : undefined;
            const explanationMessage = orderStatusText
                ? this.translations[orderStatusText.orderStatusLabel]
                : textForNotReturn;

            return explanationMessage;
        };

        return {
            [OrderStatusType.ORDER_PROCESSING]: {
                status: hasReturnOrNotReturnText(orderProcessing, returnInitiated),
                explanation: getExplanationMessage(orderStatusProcessingMessage),
                description: yourOrderIsProcessing,
                subDescription: orderStatusProcessingInstruction,
                deliveryMethod: deliveryMethodName,
                statusHeader: orderStatus,
                statusClassName: 'processing'
            },
            [OrderStatusType.AT_WAREHOUSE]: {
                status: hasReturnOrNotReturnText(atWarehouse, returnInitiated),
                explanation: getExplanationMessage(
                    orderStatusAtWarehouseMessage,
                    orderHasBeenReturnInitiated
                ),
                description: getDescriptionMessage(
                    yourOrderIsAtWarehouse,
                    yourOrderIsReturnInitiated
                ),
                subDescription: orderStatusAtWarehouseInstruction,
                deliveryMethod: deliveryMethodName,
                trackingUrl,
                statusHeader: orderStatus,
                statusClassName: 'warehouse'
            },
            [OrderStatusType.SHIPPED]: {
                status: hasReturnOrNotReturnText(shipped, returnShipped),
                explanation: getExplanationMessage(
                    orderStatusShippedMessage,
                    orderHasBeenReturnToShipped
                ),
                description: getDescriptionMessage(yourOrderIsShipped, yourOrderIsReturnToShipped),
                subDescription: orderStatusShippedInstruction,
                shippedDate: shippedDateFormattedString,
                trackingUrl,
                statusHeader: estimatedDelivery,
                statusClassName: 'shipped'
            },
            [OrderStatusType.DELAYED]: {
                status: delayed,
                explanation: orderStatusDelayedMessage,
                description: getDescriptionMessage(yourOrderIsDelayed),
                subDescription: orderStatusDelayedInstruction,
                statusHeader: estimatedDelivery,
                statusClassName: 'delayed'
            },
            [OrderStatusType.DELIVERED]: {
                status: hasReturnOrNotReturnText(delivered, returnReceived),
                explanation: getExplanationMessage(
                    orderStatusDeliveredMessage,
                    orderHasBeenReturnReceived
                ),
                description: getDescriptionMessage(yourOrderIsDelivered, yourOrderIsReturnReceived),
                subDescription: orderStatusDeliveredInstruction,
                trackingUrl,
                statusHeader: hasReturnOrNotReturnText(deliveredOn, orderStatus),
                statusClassName: 'delivered'
            },
            [OrderStatusType.RETURN_IN_PROGRESS]: {
                status: returnInProgress,
                explanation: orderStatusReturnInProgressMessage,
                description: yourOrderIsReturnInProgress,
                subDescription: orderStatusReturnInProgressInstruction,
                trackingUrl,
                statusHeader: orderStatus,
                statusClassName: 'return'
            },
            [OrderStatusType.CANCELLED]: {
                status: cancelled,
                explanation: getExplanationMessage(orderStatusCancelledMessage),
                description: yourOrderIsCancelled,
                subDescription: orderStatusCancelledInstruction,
                statusHeader: orderStatus,
                statusClassName: 'cancelled'
            },
            [OrderStatusType.UNDELIVERABLE]: {
                status: unableToDeliver,
                explanation: orderHasBeenUnableToDeliver,
                description: yourOrderIsUnableToDeliver,
                subDescription: orderStatusUnableToDeliverInstruction,
                statusHeader: orderStatus,
                statusClassName: 'undeliverable'
            }
        };
    }

    public getOrderStatusInfo(params: IGetOrderStatusInfoMap) {
        return this.getOrderStatusInfoMap(params)[this.orderStatusType];
    }

    @computed
    private get displayStatuses(): IOrderStatusCode[] {
        const filterList =
            this.orderStatusType !== OrderStatusType.RETURN_IN_PROGRESS
                ? PROGRESS_MILESTONES_BY_STATUS[this.orderStatusType]
                : false;
        if (filterList && this.isVerticalTMOProgressBarDisplay) {
            return this.orderStatusCodeMap.filter((item) =>
                filterList.includes(item.orderStatusMappedType)
            );
        } else {
            return this.orderStatusCodeMap.filter((item) => item.showInProgress);
        }
    }
}
